import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    LsqApiServiceListSource,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    SetRelationshipEligibilityRequest,
    SetRelationshipEligibilityService
} from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import { SupplierDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/supplier-dto';
import {
    ListRelationshipKeysRequest,
    ListRelationshipKeysService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-suppliers-list/list-relationship-keys';
import {
    ListResponseSupplierDto,
    ListSuppliersRequest,
    ListSuppliersService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-suppliers-list/list-suppliers';
import {
    SetActiveFlagRequest,
    SetActiveFlagService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-suppliers-list/set-active-flag';
import {
    SetScfEnabledFlagRequest,
    SetScfEnabledFlagService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-suppliers-list/set-scf-enable-flag';
import { Guid } from '@lsq-frontend/remote-services/enterprise/common';
import { CheckableRowsService, CheckableRowsServiceFactory } from '@lsq-frontend/common-ui';
import { ReplaySubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
@Injectable()
export class AdminSuppliersService {
    public checkableRowsService: CheckableRowsService;

    private _filterChangeSubject = new ReplaySubject<{
        buyerName: string;
        supplierName: string;
    }>(1);

    public dataSource: LsqApiServiceListSource<
        ListResponseSupplierDto,
        ListSuppliersRequest,
        SupplierDto
    >;

    public supplierFiltersFormGroup = new FormGroup({
        supplierName: new FormControl(),
        buyerName: new FormControl()
    });

    constructor(
        private _listSuppliersService: ListSuppliersService,
        private _listRelationshipKeysService: ListRelationshipKeysService,
        private _setRelationshipEligibilityService: SetRelationshipEligibilityService, // TODO: Replace with new bulk action service once it is created.
        private _checkableRowsServiceFactory: CheckableRowsServiceFactory,
        private _setActiveFlagService: SetActiveFlagService,
        private _setScfEnabledFlagService: SetScfEnabledFlagService,
        private _userService: UserService
    ) {
        this._userService.logout$.subscribe(() => {
            this.dataSource = null;
            this.supplierFiltersFormGroup.reset();
        });
    }

    public initializeSuppliersListSource(): void {
        if (!this.dataSource) {
            this.dataSource = new LsqApiServiceListSource<
                ListResponseSupplierDto,
                ListSuppliersRequest,
                SupplierDto
            >(
                this._listSuppliersService,
                new ListSuppliersRequest({
                    pagination: {
                        sortColumn: 'dateAdded',
                        sortDirection: SortDirectionFinancing.DESCENDING,
                        skip: 0,
                        take: 25
                    }
                })
            );
        }
    }

    public async initCheckableService(): Promise<void> {
        // Get all the relationship keys, unfiltered.
        let allRelationshipKeys: string[];
        allRelationshipKeys = (
            await this._listRelationshipKeysService.executeOnce(new ListRelationshipKeysRequest())
        ).relationshipKeys;

        this.checkableRowsService = this._checkableRowsServiceFactory.create();
        this.checkableRowsService.addRows(allRelationshipKeys);

        // Whenever the request filters are changed, fetch all the relationships that match those filters.
        // This is used by the "Select All" button to select all that match the current filters.
        this._filterChangeSubject
            .pipe(
                switchMap(async (newFilters) => {
                    this.checkableRowsService.setLoading(true);
                    return (
                        await this._listRelationshipKeysService.executeOnce(
                            new ListRelationshipKeysRequest({
                                filter: {
                                    supplierName: newFilters.supplierName,
                                    buyerName: newFilters.buyerName
                                }
                            })
                        )
                    ).relationshipKeys;
                })
            )
            .subscribe((allFilteredRelationshipKeys) => {
                this.checkableRowsService.setCurrentAllFilteredRowKeys(allFilteredRelationshipKeys);
                this.checkableRowsService.setLoading(false);
            });

        this.dataSource.rows$.subscribe((currentVisibleRows) => {
            this.checkableRowsService.setCurrentPageKeys(
                currentVisibleRows.map((row) => row.relationshipKey)
            );
        });
    }

    public dispatchFilterChange(newFilters: { buyerName: string; supplierName: string }): void {
        this._filterChangeSubject.next(newFilters);
    }

    public async setSuppliersActiveState(supplierKeys: Guid[], active: boolean): Promise<void> {
        await this._setActiveFlagService.executeOnce(
            new SetActiveFlagRequest({
                supplierKey: supplierKeys[0],
                active
            })
        );
    }

    public async setSuppliersEligibility(
        { supplierKey, relationshipKey }, // TEMP while we use the old eligibility endpoint.
        // relationshipKeys: Guid[], TODO: The bulk endpoint shouldn't need the supplier keys, the relationship keys should be enough.
        eligible: boolean
    ): Promise<void> {
        // TODO: Use new bulk action endpoint.
        await this._setRelationshipEligibilityService.executeOnce(
            new SetRelationshipEligibilityRequest({
                supplierKey,
                relationshipKey,
                eligibility: eligible
            })
        );
    }

    public async setSuppliersScfEnabledState(
        relationshipKey: Guid,
        scfEnabled: number
    ): Promise<void> {
        await this._setScfEnabledFlagService.executeOnce(
            new SetScfEnabledFlagRequest({
                relationshipKey: relationshipKey,
                scfEnabled: scfEnabled
            })
        );
    }
}
